import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { Loader2, AlertTriangle } from "lucide-react";
import { Logo } from "@/components/Logo";

// Lightweight client-side preview filter (real check happens on backend).
// Uses a small high-confidence set so users get instant feedback without
// duplicating the full server list.
const CLIENT_BAD_HINTS = [
    "fuck", "shit", "bitch", "cunt", "nigger", "nigga", "fag", "retard",
    "rape", "kys", "pedo", "loli", "admin", "support", "owner", "moderator", "staff",
];
const _normalize = (s) => (s || "").toLowerCase()
    .replace(/0/g, "o").replace(/1/g, "i").replace(/!/g, "i")
    .replace(/3/g, "e").replace(/4/g, "a").replace(/@/g, "a")
    .replace(/5/g, "s").replace(/\$/g, "s")
    .replace(/[^a-z]/g, "");

export default function Signup() {
    const { signup } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const nameWarning = useMemo(() => {
        const trimmed = (name || "").trim();
        if (!trimmed) return null;
        if (trimmed.length === 1) return "Display name must be at least 2 characters";
        if (trimmed.length > 30) return "Display name must be 30 characters or fewer";
        const norm = _normalize(trimmed);
        for (const bad of CLIENT_BAD_HINTS) {
            if (norm.includes(bad)) return "That name contains language we don't allow. Pick something else.";
        }
        return null;
    }, [name]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        if (nameWarning) {
            toast.error(nameWarning);
            return;
        }
        setLoading(true);
        try {
            await signup(email, password, name || null);
            toast.success("Account created. Welcome to HOTSLIP!");
            nav("/");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Signup failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen pp-bg-0 text-white">
            <Nav />
            <section className="py-16 sm:py-24">
                <div className="max-w-md mx-auto px-4">
                    <div className="flex items-center gap-3 mb-6">
                        <Logo size={36} />
                        <div>
                            <p className="text-xs font-mono uppercase text-[#22c55e] tracking-widest">Join HOTSLIP</p>
                            <h1 className="font-display text-3xl tracking-widest">SIGN UP</h1>
                        </div>
                    </div>
                    <Card className="pp-bg-1 border-[#242436] text-white">
                        <CardContent className="p-6">
                            <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
                                <div>
                                    <Label htmlFor="name" className="text-[#b7b7c6]">Display name (optional)</Label>
                                    <Input id="name" value={name} onChange={(e) => setName(e.target.value.slice(0, 30))} placeholder="Goblin Hunter" className={`mt-1 pp-bg-0 border-[#242436] text-white placeholder:text-[#5e5e72] ${nameWarning ? "border-[#ef4444]" : ""}`} data-testid="signup-name-input" />
                                    {nameWarning ? (
                                        <p className="mt-1 text-xs text-[#ef4444] inline-flex items-center gap-1" data-testid="signup-name-warning">
                                            <AlertTriangle className="w-3 h-3" /> {nameWarning}
                                        </p>
                                    ) : (
                                        <p className="mt-1 text-[10px] font-mono text-[#8b8ba0]">2-30 chars. No slurs, profanity, or staff impersonation.</p>
                                    )}
                                </div>
                                <div>
                                    <Label htmlFor="email" className="text-[#b7b7c6]">Email</Label>
                                    <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="mt-1 pp-bg-0 border-[#242436] text-white placeholder:text-[#5e5e72]" data-testid="auth-email-input" />
                                </div>
                                <div>
                                    <Label htmlFor="password" className="text-[#b7b7c6]">Password</Label>
                                    <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="min 6 chars" className="mt-1 pp-bg-0 border-[#242436] text-white placeholder:text-[#5e5e72]" data-testid="auth-password-input" />
                                </div>
                                <Button type="submit" disabled={loading} className="w-full h-11 bg-[#22c55e] hover:brightness-110 text-[#07130b] font-semibold" data-testid="auth-submit-button">
                                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Account"}
                                </Button>
                            </form>
                            <p className="mt-4 text-sm text-[#b7b7c6]">
                                Already have an account?{" "}
                                <Link to="/login" className="text-[#a855f7] hover:underline" data-testid="signup-to-login-link">Log in</Link>
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </section>
            <Footer />
        </div>
    );
}
